import { useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { Stack } from 'expo-router'
import { StatusBar } from 'expo-status-bar'
import { SafeAreaProvider } from 'react-native-safe-area-context'
import { TamaguiProvider, Theme, type ThemeName } from 'tamagui'

import { config } from '../tamagui.config'
import { AppProvider, useUiPrefs } from '../src/i18n'
import { DEFAULT_ACCENT } from '../src/theme/accents'
import { QueryProvider } from '../src/providers/QueryProvider'
import { AuthProvider } from '../src/auth/AuthProvider'
import { AuthGate } from '../src/auth/AuthGate'
import { ErrorBoundary } from '../src/ui'
import { initMonitoring } from '../src/lib/monitoring'

// Как можно раньше, до первого рендера — пустой DSN/endpoint делает вызов no-op.
initMonitoring()

/**
 * Root layout. `AppProvider` (lang + theme + accent) sits ABOVE Tamagui so the
 * theme/accent toggles re-theme the whole portal via the <Theme> wrappers.
 * Order: App(lang/theme/accent) → Tamagui → SafeArea → Query(data) → Auth → gate.
 *
 * RootLayout оборачивает всё это в <ErrorBoundary> — самый внешний слой (см.
 * src/ui/ErrorBoundary.tsx): краш репортится даже до монтирования AppProvider/
 * Tamagui, поэтому у fallback-экрана свой TamaguiProvider и нет i18n-контекста.
 *
 * Web is a STATIC export (`web.output: 'static'`): each route is prerendered in
 * Node with the *default* theme (no localStorage there). The persisted theme +
 * accent are applied only AFTER mount — the first client paint deliberately
 * matches the prerendered defaults so hydration is clean, then we switch to the
 * real values, which forces React to patch the Tamagui theme class (a className
 * the hydration pass does NOT reconcile — that's why the accent looked "stuck"
 * on reload while the inline-styled sidebar updated fine).
 */
function Themed() {
  const { theme, accent } = useUiPrefs()
  // Native has no prerender/hydration, so apply immediately; web defers one tick.
  const [hydrated, setHydrated] = useState(Platform.OS !== 'web')
  useEffect(() => setHydrated(true), [])

  const appliedTheme = hydrated ? theme : 'light'
  const appliedAccent = hydrated ? accent : DEFAULT_ACCENT

  return (
    <TamaguiProvider config={config} defaultTheme={appliedTheme}>
      {/* base light/dark, then the accent sub-theme (`${theme}_${accent}`) so the
          selected primary re-skins the whole portal — see src/theme/accents.ts. */}
      <Theme name={appliedTheme}>
        {/* bare sub-name (`navy`) resolves to `${theme}_navy` at runtime; the
            ThemeName union only lists full keys, hence the cast. */}
        <Theme name={appliedAccent as ThemeName}>
          <SafeAreaProvider>
            <QueryProvider>
              <AuthProvider>
                <AuthGate>
                  <Stack screenOptions={{ headerShown: false, contentStyle: { flex: 1 } }}>
                    <Stack.Screen name="(portal)" />
                    <Stack.Screen name="login" options={{ presentation: 'modal' }} />
                    {/* Публичная страница подписания для контрагента: вне (portal), без
                        навигации и профиля — вход в портал ей не нужен (см. AuthGate). */}
                    <Stack.Screen name="s/[token]" />
                  </Stack>
                </AuthGate>
                <StatusBar style={appliedTheme === 'dark' ? 'light' : 'dark'} />
              </AuthProvider>
            </QueryProvider>
          </SafeAreaProvider>
        </Theme>
      </Theme>
    </TamaguiProvider>
  )
}

export default function RootLayout() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <Themed />
      </AppProvider>
    </ErrorBoundary>
  )
}
